/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import RadioGroup from '@material-ui/core/RadioGroup';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import classnames from 'classnames';
import {findCurrentAccountMatch, getParentAccounts} from '@ion/core/src/entities/Accounts';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {translate} from '@ion/core/src/locale/locale';
import {AccountLink, Crumb} from '@ion/core/src/types/entities';
import useMedia from '../../hooks/useMedia';
import {getGlobalProp} from '../../props/GlobalPropsConfig';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor, dlsShadow, dlsTypography} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';
import {resetValue} from '../../utils/ComponentUtils';
import Breadcrumb from '../breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../breadcrumbs/BreadcrumbUtils';
import CloseButton from '../buttons/CloseButton';
import {PrimaryButton} from '../buttons/PrimaryButton';
import Chip from '../chip/Chip';
import {IonSpinner} from '../loading/IonSpinner';
import GlobalGroupChooserSearch from './GlobalGroupChooserSearch';
import GlobalGroupsSearchCount from './GlobalGroupsSearchCount';
import {AutoSelectClient} from './GroupChooserAccordion';
import {getCrumbs} from './GroupChooserCrumbs';
import GroupsHierarchyDisplay from './GroupsHierarchyDisplay';
import GroupsQueryResult from './GroupsQueryResults';

export interface GlobalGroupChooserContentProps extends AutoSelectClient {
  groups: AccountLink[];
  currentGroup: AccountLink;
  onGroupApply?: (value: AccountLink) => void;
  onCloseDrawer?: MouseEventHandler<HTMLButtonElement>;
  title?: string;
  currentTitle?: string;
  drawerOpen?: boolean;
  hideParentAccount?: boolean;
  includeBreadcrumbs?: boolean;
  resetDeviceMovedAccount?: Function;
  hasAssetDeviceMove?: boolean;
  onContentScrollToTop?: Function;
  allCrumbsClickable?: boolean;
  onlyAccountId?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  groupChooserLoader : {
    height         : 'calc(100vh - 375px)',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  groupChooserHeader : toggleThemeStyle({
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    borderBottom   : `solid 1px ${dlsColor('grey5')}`,
    padding        : '0px 32px',
    margin         : '0px -32px'
  }, {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between'
  }),
  groupChooserTitle : toggleThemeStyle({
    textTransform : 'uppercase',
    fontWeight    : 500,
    letterSpacing : '1px'
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  selectedGroup : toggleThemeStyle({

  }, {
    top      : '8px',
    position : 'relative'
  }),
  chooseGroup : {
    bottom                         : toggleThemeStyle('unset', '10px'),
    margin                         : toggleThemeStyle('20px 0 20px -8px', 'unset'),
    height                         : 'fit-content',
    position                       : toggleThemeStyle('unset', 'relative'),
    overflowY                      : 'hidden',
    overflowX                      : 'hidden',
    paddingBottom                  : toggleThemeStyle('0px', '24px'),
    [theme.breakpoints.down('sm')] : {
      marginBottom : '5px',
      marginTop    : '0px'
    }
  },
  chooseGroupCTC : toggleThemeStyle({
    height                         : 'calc(100vh - 360px)',
    overflowY                      : 'auto',
    overflowX                      : 'auto',
    [theme.breakpoints.down('sm')] : {
      marginBottom : '5px',
      height       : 'calc(100vh - 373px)',
      marginTop    : '50px'
    }
  }, {
    height                         : 'calc(100vh - 360px)',
    overflowY                      : 'hidden',
    overflowX                      : 'hidden',
    padding                        : '24px 0',
    [theme.breakpoints.down('sm')] : {
      marginBottom : '5px',
      height       : 'calc(100vh - 373px)',
      marginTop    : '50px'
    }
  }),
  chooseGroupTitle : toggleThemeStyle({
    ...dlsTypography('h4'),
    letterSpacing : '1px',
    margin        : '10px 0px'
  }, {
    color         : dlsColor('grey1'),
    fontFamily    : 'Inter',
    fontSize      : '16px',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  radioGroup : {
    flexWrap                       : 'nowrap',
    height                         : toggleThemeStyle('calc(100vh - 360px)', 'calc(100vh - 300px)'),
    overflowY                      : 'hidden',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 376px)'
    }
  },
  scrollContainer : {
    overflowY : 'auto'
  },
  selectedGroupTitle : toggleThemeStyle({
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px',
    ...dlsAlt('typography', 'label1')
  }, {
    color         : dlsColor('grey1'),
    display       : 'flex',
    fontFamily    : 'Inter',
    fontSize      : '14px',
    alignItems    : 'center',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '0px'
  }),
  chips : toggleThemeStyle({
    minHeight  : '56px',
    display    : 'flex',
    alignItems : 'center'
  }, {
    left       : '6px',
    display    : 'flex',
    position   : 'relative',
    minHeight  : '56px',
    alignItems : 'center'
  }),
  chipsCTC : {
    display    : 'flex',
    alignItems : 'center'
  },
  groupChooserFooter : toggleThemeStyle({
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }, {
    display   : 'block',
    height    : '64px',
    margin    : '0 -18px',
    padding   : '0 16px',
    boxShadow : dlsShadow(5.2, 5.3)
  }),
  breadcrumbWrapper : {
    '& div' : {
      maxWidth  : 'calc(100vw - 52px)',
      overflowY : 'auto',
      padding   : 10,
      '& nav'   : {
        alignSelf : 'flex-start'
      }
    }
  },
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  breadcrumbRoot : {
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  currentCloseIcon : {
    left     : '12px',
    position : 'relative'
  },
  applyButton : {
    top      : '20px',
    position : 'relative'
  },
  searchRoot : {
    bottom         : '2px',
    display        : 'flex',
    position       : 'relative',
    alignItems     : 'center',
    justifyContent : 'space-between'
  },
  searchContainer : {
    gap             : '4px',
    display         : 'flex',
    padding         : '0 8px 0 8px',
    flexWrap        : 'wrap',
    minHeight       : '24px',
    alignItems      : 'center',
    borderRadius    : '4px',
    backgroundColor : dlsColor('grey6')
  }
}));

interface CrumbType {
  click: boolean;
  href: string;
  label: string;
  onClick: () => void;
}

/**
 * Remove Choose Account from the breadcrumbs list
 *
 * @param crumbs array of crumbs
 * @returns any[] array of crumbs without 'Choose Account'
 *
 */
const removeDefaultCrumb = (crumbs: CrumbType[], defaultGroupText: string) => {
  return crumbs.filter(({label}: CrumbType) => label !== defaultGroupText);
};

/**
 * Chooser that takes an array of potential groups and renders a Desktop Accordion
 * or Mobile Carousel Chooser component.
 *
 * @param groups groups in user's hierarchy that can be chosen
 * @param currentGroup group that is currently chosen as global group
 * @param title title of the chooser
 * @param onGroupApply callback that gets fired when user clicks to apply group filter
 *
 * ```typescript
 * import {GlobalGroupChooserContent} from '@ion/dls/src/components/groupchooser/GlobalGroupChooserContent';
 *
 * <GroupChooser groups={groups}
 *   currentGroups={accountLink}
 *   title='Change Group'
 *   onGroupApply={changeGroup} />
 * ```
 */
export const GlobalGroupChooserContent = ({
  groups,
  currentGroup,
  onGroupApply,
  onCloseDrawer,
  resetDeviceMovedAccount,
  onContentScrollToTop,
  title = translate('common:editors.shared.changeGroup'),
  currentTitle = translate('common:editors.shared.groupChooser'),
  onlyAccountId = false,
  hideParentAccount = false,
  includeBreadcrumbs = false,
  hasAssetDeviceMove = false,
  allCrumbsClickable = false,
  disableAutoSelect = false
}: GlobalGroupChooserContentProps) => {
  const classes                                       = useStyles();
  const [radioValue, setRadioValue]                   = useState(null);
  const [expansionList, setExpansionList]             = useState([]);
  const [searchValue, setSearchValue]                 = useState('');
  const [selectedSearchValue, setSelectedSearchValue] = useState({title: '', href: ''});
  const [clearSearch, setClearSearch]                 = useState(false);
  const [renderingData, setRenderingData]             = useState(false);
  const {isPhone}                                     = useMedia();
  const {title: groupTitle, subAccounts, href}        = groups[0];
  const mainListLength                                = subAccounts?.length || 0;
  const [crumbValue, setCrumbValue]                   = useState(null);
  const showCrumbs                                    = !isPhone && includeBreadcrumbs;
  const groupChooserSearchResultsRef                  = useRef(null);
  const groupChooserSearchRadioRef                    = useRef(null);
  const subaccountTimeoutRef                          = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const minMatchCharLength = getGlobalProp('global-groups.search.minMatchCharLength', 3);

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };
  const initialCrumb = {
    label   : translate('common:editors.shared.chooseGroup'),
    href    : href,
    click   : true,
    onClick : function() {
      handleCrumbClick({
        title : groupTitle,
        href  : href,
        subAccounts
      });
    }
  };
  const rawCrumbs                 = useRef([initialCrumb]);
  const [crumbData, setCrumbData] = useState<Crumb[]>(
    defineBreadcrumbs({
      allCrumbsClickable,
      crumbs  : [initialCrumb],
      classes : crumbClasses
    })
  );

  useEffect(() => {
    const initialCrumbs = getCrumbs({
      group            : groups[0],
      currentGroup     : currentGroup,
      radioValue       : radioValue,
      initialCrumb     : initialCrumb,
      handleCrumbClick : handleCrumbClick
    });
    const crumbs = removeDefaultCrumb(initialCrumbs, translate('common:editors.shared.chooseGroup'));
    rawCrumbs.current = crumbs;
    setCrumbData(defineBreadcrumbs({
      allCrumbsClickable,
      crumbs  : crumbs,
      classes : crumbClasses
    }));
  }, []);

  useEffect(() => () => {
    if (subaccountTimeoutRef.current) {
      clearTimeout(subaccountTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const [match] = findCurrentAccountMatch(
      groups,
      selectedSearchValue.title ? selectedSearchValue
        : radioValue ? {href: radioValue} : currentGroup
    );
    if (match) {
      if (!selectedSearchValue.title) {
        setRadioValue(match.href);
      }
      setExpansionList(getParentAccounts(match, groups, 'href'));
    }
  }, [currentGroup, radioValue, selectedSearchValue]);

  const onRadioChange = useCallback(function(e, value) {
    const [match] = findCurrentAccountMatch(groups, {href: value});
    setRadioValue(match.href);
  }, [groups]);

  const handleGroupApply = useCallback(function() {
    onGroupApply?.(findCurrentAccountMatch(groups, {href: radioValue})[0]);
    setSelectedSearchValue({title: '', href: ''});
    setCrumbValue(null);
    hasAssetDeviceMove && resetDeviceMovedAccount?.(null);
  }, [groups, onGroupApply, hasAssetDeviceMove, resetDeviceMovedAccount, radioValue]);

  const clearSearchValue = useCallback(function() {
    resetValue({
      fnCall       : setClearSearch,
      initialValue : true,
      resetValue   : false
    });
  }, []);

  const handleSearchChange = useCallback(function(value) {
    setSearchValue(value);
    setSelectedSearchValue({title: '', href: ''});
  }, []);

  const handleCrumbClick = useCallback(function({title, href}) {
    const crumbIndex = rawCrumbs.current?.findIndex(crumb => crumb.label === title);
    rawCrumbs.current.splice(crumbIndex);
    setCrumbValue(href);
    setSelectedSearchValue({title, href});
    setCrumbData(defineBreadcrumbs({
      allCrumbsClickable,
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
  }, [allCrumbsClickable]);

  const handleBack = useCallback(function() {
    rawCrumbs.current.pop();
    const crumbsLength = rawCrumbs?.current?.length;
    setCrumbValue(rawCrumbs?.current[crumbsLength - 1]?.href);
    setCrumbData(defineBreadcrumbs({
      allCrumbsClickable,
      crumbs  : rawCrumbs.current,
      classes : crumbClasses
    }));
  }, [allCrumbsClickable, crumbClasses]);

  const onViewSubaccounts = useCallback(function(item: any, isLoading?: boolean) {
    setSelectedSearchValue(item);
    if (isLoading) {
      if (subaccountTimeoutRef.current) {
        clearTimeout(subaccountTimeoutRef.current);
      }
      setRenderingData(true);
      subaccountTimeoutRef.current = setTimeout(() => {
        setRenderingData(false);
      }, 1000);
    }
  }, []);

  const chipLabel = EntityLabel.get(findCurrentAccountMatch(groups, {href: radioValue})[0]);

  useEffect(() => {
    if (!renderingData && searchValue) {
      const scrollToTop = ref => {
        ref?.current?.scrollTo({
          top      : 0,
          behavior : 'smooth'
        });
      };
      scrollToTop(groupChooserSearchRadioRef);
      scrollToTop(groupChooserSearchResultsRef);
    }
  }, [searchValue, renderingData]);

  return (
    <div>
      {
        (!isPhone || isCurrentTheme) && (
          <div className={classes.groupChooserHeader}>
            <h2 className={classes.groupChooserTitle}>
              {isCurrentTheme ? currentTitle : title}
            </h2>
            <Render.If condition={isCurrentTheme}>
              <div className={classes.currentCloseIcon}>
                <CloseButton onClick={onCloseDrawer} />
              </div>
            </Render.If>
          </div>
        )
      }
      <GlobalGroupChooserSearch setSearchValue={handleSearchChange}
        clearSearch={clearSearch}
        listLength={mainListLength}
        onlyAccountId={onlyAccountId}
        inputLength={includeBreadcrumbs ? 1 : minMatchCharLength}
        setRenderingData={setRenderingData} />
      <div className={classes.selectedGroup}>
        <div className={classes.selectedGroupTitle}>
          {translate('common:editors.shared.selectedGroup')}
          <div className={showCrumbs ? classes.chipsCTC : classes.chips}>
            {
              chipLabel && (
                <Chip label={chipLabel}
                  isCurrentTheme={isCurrentTheme}
                  size='small'
                  borderRadius='4px'
                  labelColor={dlsColor('grey2')} />
              )
            }
          </div>
        </div>
      </div>
      {
        renderingData ? (
          <div className={classes.groupChooserLoader}>
            <IonSpinner />
          </div>
        ) : (
          <>
            {showCrumbs && (
              <div className={classes.breadcrumbWrapper}>
                <Breadcrumb icon={rawCrumbs.current?.length > 1 && <ChevronLeft />}
                  itemsBeforeCollapse={hideParentAccount ? 0 : 1}
                  iconClick={handleBack}
                  breadcrumbClass={classes.breadcrumbRoot}
                  height={'auto'}>
                  {crumbData}
                </Breadcrumb>
              </div>)}
            {
              <div className={isCurrentTheme ? classes.searchRoot : ''}>
                <Render.If condition={!isPhone}>
                  <h4 className={classes.chooseGroupTitle}>
                    {translate('common:editors.shared.chooseGroup')}
                  </h4>
                </Render.If>
                <Render.If condition={isCurrentTheme && searchValue && !selectedSearchValue.title}>
                  <div className={classes.searchContainer}>
                    <GlobalGroupsSearchCount searchValue={searchValue} />
                  </div>
                </Render.If>
              </div>
            }
            <div ref={groupChooserSearchResultsRef}
              className={showCrumbs ? classes.chooseGroupCTC : classes.chooseGroup}>
              <RadioGroup ref={groupChooserSearchRadioRef} className={
                classnames({
                  [classes.radioGroup]      : true,
                  [classes.scrollContainer] : getGlobalProp('GlobalGroupChooser.desktop.useDrillDownHierarchy', false) as boolean ? (searchValue && !selectedSearchValue?.title) : true
                })}
              onChange={onRadioChange}
              value={radioValue}>
                {
                  searchValue && !selectedSearchValue.title ? (
                    <GroupsQueryResult searchValue={searchValue}
                      hideParentAccount={hideParentAccount}
                      onViewSubaccounts={onViewSubaccounts}
                      handleCrumbClick={handleCrumbClick}
                      onContentScrollToTop={onContentScrollToTop}/>
                  ) : (
                    <GroupsHierarchyDisplay groups={groups}
                      radioValue={radioValue}
                      setRadioValue={setRadioValue}
                      selectedSearchValue={selectedSearchValue}
                      crumbValue={crumbValue}
                      expansionList={expansionList}
                      mainListLength={mainListLength}
                      disableAutoSelect={disableAutoSelect} />
                  )
                }
              </RadioGroup>
            </div>
            <div className={classes.groupChooserFooter}>
              <PrimaryButton fullWidth
                className={isCurrentTheme ? classes.applyButton : ''}
                disabled={!radioValue}
                onClick={handleGroupApply}>
                {translate('common:editors.shared.apply')}
              </PrimaryButton>
            </div>
          </>
        )
      }
    </div>
  );
};

export default GlobalGroupChooserContent;
